import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  CardNumberElement, 
  CardExpiryElement, 
  CardCvcElement,
  useStripe, 
  useElements 
} from '@stripe/react-stripe-js';

const HotelStripePaymentForm = ({ amount, onSuccess, onError, clientSecret, onInitializePayment, isInitialized, loading, error: initError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [waitingForInit, setWaitingForInit] = useState(false);
  const [shouldAutoProcess, setShouldAutoProcess] = useState(false);
  const processPaymentRef = useRef(false);

  const processPayment = useCallback(async () => {
    if (!stripe || !elements || !clientSecret) {
      console.error('[Hotel Stripe Form] Cannot process payment - missing:', {
        stripe: !!stripe,
        elements: !!elements,
        clientSecret: !!clientSecret
      });
      setError('Payment system not ready. Please try again.');
      return;
    }

    console.log('[Hotel Stripe Form] Starting payment processing...');
    setProcessing(true);
    setError(null);

    try {
      const cardNumberElement = elements.getElement(CardNumberElement);

      if (!cardNumberElement) {
        throw new Error('Card element not found. Please enter your card details.');
      }

      console.log('[Hotel Stripe Form] Creating payment method...');
      // Create payment method
      const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardNumberElement,
      });

      if (pmError) {
        console.error('[Hotel Stripe Form] Payment method creation error:', pmError);
        throw new Error(pmError.message || 'Failed to create payment method');
      }

      if (!paymentMethod) {
        throw new Error('Failed to create payment method');
      }

      console.log('[Hotel Stripe Form] Payment method created:', paymentMethod.id);
      console.log('[Hotel Stripe Form] Confirming payment with Stripe...');
      
      // Confirm payment with Stripe
      const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
        clientSecret,
        {
          payment_method: paymentMethod.id,
        }
      );

      // Stripe sometimes returns an error object with a payment_intent that is actually succeeded.
      // This can happen with code 'payment_intent_unexpected_state'.
      const finalPaymentIntent = paymentIntent || (confirmError && confirmError.payment_intent) || null;

      if (confirmError && (!finalPaymentIntent || finalPaymentIntent.status !== 'succeeded')) {
        console.error('[Hotel Stripe Form] Payment confirmation error:', confirmError);
        throw new Error(confirmError.message || 'Payment confirmation failed');
      }

      if (!finalPaymentIntent) {
        throw new Error('Payment intent not returned');
      }

      console.log('[Hotel Stripe Form] Payment intent status:', finalPaymentIntent.status);

      if (finalPaymentIntent.status === 'succeeded') {
        console.log('[Hotel Stripe Form] ✅ Payment succeeded! Payment Intent ID:', finalPaymentIntent.id);
        // Payment successful - call onSuccess with payment info
        onSuccess({
          payment: {
            paymentIntentId: finalPaymentIntent.id,
            paymentMethod: 'stripe',
            amount: finalPaymentIntent.amount / 100,
            currency: finalPaymentIntent.currency,
            status: 'succeeded'
          }
        });
      } else {
        console.warn('[Hotel Stripe Form] Payment intent status is not succeeded:', finalPaymentIntent.status);
        throw new Error(`Payment not completed. Status: ${finalPaymentIntent.status}`);
      }
    } catch (err) {
      console.error('[Hotel Stripe Form] Payment processing error:', err);
      const errorMessage = err.message || 'An error occurred during payment. Please check your card details and try again.';
      setError(errorMessage);
      if (onError) {
        onError(err);
      }
    } finally {
      setProcessing(false);
      processPaymentRef.current = false;
    }
  }, [stripe, elements, clientSecret, onSuccess, onError]);

  // Auto-process payment after initialization if user had clicked Pay Now
  useEffect(() => {
    if (waitingForInit && clientSecret && stripe && elements) {
      console.log('[Hotel Stripe Form] ClientSecret received, Elements ready.');
      setWaitingForInit(false);
      
      // If user had clicked Pay Now before initialization, auto-process now
      if (shouldAutoProcess) {
        console.log('[Hotel Stripe Form] Auto-processing payment after initialization...');
        setShouldAutoProcess(false);
        // Small delay to ensure Elements are fully ready
        setTimeout(() => {
          processPayment();
        }, 500);
      } else {
        setProcessing(false); // Reset processing state so user can click Pay Now
      }
    }
  }, [clientSecret, waitingForInit, stripe, elements, shouldAutoProcess, processPayment]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent event from bubbling to parent form
    
    console.log('[Hotel Stripe Form] Form submitted, clientSecret:', !!clientSecret, 'stripe:', !!stripe, 'elements:', !!elements);

    if (!stripe || !elements) {
      console.log('[Hotel Stripe Form] Stripe or elements not ready');
      return false;
    }

    // If payment intent is not initialized, initialize it first
    if (!clientSecret && onInitializePayment) {
      console.log('[Hotel Stripe Form] Initializing payment intent...');
      setProcessing(true);
      setError(null);
      setWaitingForInit(true);
      setShouldAutoProcess(true); // Mark that we should auto-process after initialization
      try {
        await onInitializePayment();
        console.log('[Hotel Stripe Form] Payment intent initialized, waiting for clientSecret...');
        // Don't process yet - wait for clientSecret to be set and Elements to be ready
        // The useEffect will auto-process when clientSecret is available
        return false;
      } catch (err) {
        console.error('[Hotel Stripe Form] Initialization error:', err);
        setError(err.message || 'Failed to initialize payment');
        setProcessing(false);
        setWaitingForInit(false);
        setShouldAutoProcess(false);
        return false;
      }
    }

    if (!clientSecret) {
      console.log('[Hotel Stripe Form] No clientSecret available');
      setError('Payment not initialized. Please try again.');
      return false;
    }

    console.log('[Hotel Stripe Form] Processing payment...');
    // Process payment directly if clientSecret is available
    await processPayment();
    return false;
  };

  const elementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#424770',
        fontFamily: 'Inter, sans-serif',
        '::placeholder': {
          color: '#aab7c4',
        },
      },
      invalid: {
        color: '#9e2146',
      },
    },
  };

  return (
    <form onSubmit={handleSubmit} className="stripe-payment-form" noValidate>
      <div className="form-group">
        <label className="form-label">
          Card Number <span className="required">*</span>
        </label>
        <div className="stripe-card-number-element">
          <CardNumberElement options={elementOptions} />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem' }}>
        <div className="form-group">
          <label className="form-label">
            Expiry Date <span className="required">*</span>
          </label>
          <div className="stripe-card-expiry-element">
            <CardExpiryElement options={elementOptions} />
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">
            CVV <span className="required">*</span>
          </label>
          <div className="stripe-card-cvc-element">
            <CardCvcElement options={elementOptions} />
          </div>
        </div>
      </div>

      {(error || initError) && (
        <div className="stripe-error" role="alert">
          {error || initError}
        </div>
      )}

      {loading && !isInitialized && (
        <div className="stripe-loading">
          <p>Initializing payment...</p>
        </div>
      )}

      <button
        type="submit"
        className="pay-now-button"
        disabled={!stripe || processing || (loading && !isInitialized)}
      >
        {waitingForInit ? 'Initializing Payment...' : loading && !isInitialized ? 'Initializing...' : processing ? 'Processing Payment...' : !clientSecret ? 'Initialize Payment' : `Pay ${amount}`}
      </button>
    </form>
  );
};

export default HotelStripePaymentForm;
